import React, { useState } from 'react'
import { useHistory } from 'react-router-dom'

import { FormContext } from 'react-hook-form'
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@material-ui/core'
import { reverse } from 'named-urls'
import { ExternalLink as ExternalLinkIcon } from 'react-feather'

import {
  BaseDialog,
  Container,
  ContentHeader,
  LoadingFeedback,
  Page,
} from 'components'
import {
  ActivityFormContent,
  AutomationConfirmDialog,
  FieldEditConfirmDialog,
} from './components'

import helpers from 'helpers'

import useActivityModalForm from 'hooks/useActivityModalForm'
import usePermissions from 'hooks/usePermissions'

import { routes } from 'Routes'
import constants from 'constants/index'
import schema from './schema'
import useStyles from './styles'

const ActivityModal = ({
  open,
  setOpen,
  activity = null,
  columns = [],
  users = [],
  departments = [],
  onSave,
  onRemoveAttachment,
  fullPage = false,
  isSaving = false,
  initialComments = null,
  isShow = false,
}) => {
  const {
    KANBAN_COLUMN_ROLE_FINALIZED,
    KANBAN_COLUMN_ROLE_IN_PROGRESS,
    KANBAN_COLUMN_ROLE_PENDING,
    SOURCEABLE_TYPE_COMPLAINT,
  } = constants.activities
  const FORM_ID = 'activity-modal-form'

  const classes = useStyles()
  const history = useHistory()
  const permissions = usePermissions()
  const [pendingSavePayload, setPendingSavePayload] = useState(null)
  const [pendingFieldEditPayload, setPendingFieldEditPayload] = useState(null)

  const isEdit = !!activity?.id
  const isSystemTask = !!activity?.sourceableId

  const {
    formMethods,
    initialValuesRef,
    isMultiDepartment,
    fetchedTask,
    fetchedComments,
    loadingTask,
    formReady,
  } = useActivityModalForm({
    open,
    activity,
    fullPage,
    validationSchema: schema(isSystemTask),
  })
  const { control, errors, handleSubmit } = formMethods

  const onSubmit = async (data) => {
    const snap = isEdit ? initialValuesRef.current : null
    const { unchanged, payload, nonStatusFieldChanged } =
      helpers.activities.diffAndBuildPayload(data, snap, columns, {
        KANBAN_COLUMN_ROLE_FINALIZED,
      })

    const task = fetchedTask || activity
    if (task?.sourceableType === SOURCEABLE_TYPE_COMPLAINT) {
      delete payload.responsibleIds
    }

    if (unchanged) {
      setOpen(false)
      return
    }

    const willLeaveAutomation =
      task?.autoManaged && (payload.isMoveColumn || payload.isFinalize)

    if (willLeaveAutomation) {
      setPendingSavePayload(payload)
      return
    }

    if (
      snap &&
      task?.autoManaged &&
      !task?.fieldsOverridden &&
      nonStatusFieldChanged
    ) {
      setPendingFieldEditPayload(payload)
      return
    }

    await savePayload(payload)
  }

  const savePayload = async (payload) => {
    try {
      if (onSave) await onSave(payload, activity?.id)
      setOpen(false)
    } catch {
      // onSave already showed error snackbar
    }
  }

  const confirmLeaveAutomation = async () => {
    const payload = pendingSavePayload
    setPendingSavePayload(null)
    if (payload) await savePayload(payload)
  }

  const confirmFieldEdit = async () => {
    const payload = pendingFieldEditPayload
    setPendingFieldEditPayload(null)
    if (payload) await savePayload(payload)
  }

  const currentColumn = helpers.activities.resolveCurrentColumn(
    isEdit,
    fetchedTask || activity,
    activity,
    columns,
    {
      KANBAN_COLUMN_ROLE_PENDING,
      KANBAN_COLUMN_ROLE_FINALIZED,
      KANBAN_COLUMN_ROLE_IN_PROGRESS,
    },
  )

  const title = activity?.id
    ? `#${String(activity.id).padStart(5, '0')}`
    : 'Criar Atividade'

  const pageTitle = activity?.id
    ? `Detalhes da atividade - #${String(activity.id).padStart(5, '0')}`
    : title

  const isCrossBoard = !!(fetchedTask || activity)?.targetCompanyId
  const canEdit =
    !isShow &&
    !isCrossBoard &&
    permissions.some([
      constants.permissions.KANBAN_TASKS.MANAGE,
      constants.permissions.KANBAN_TASKS.FINALIZE,
    ])

  const expandPath =
    activity?.id && !fullPage
      ? canEdit
        ? routes.activitiesPanel?.edit
          ? reverse(routes.activitiesPanel.edit, { id: activity.id })
          : null
        : routes.activitiesPanel?.show
        ? reverse(routes.activitiesPanel.show, { id: activity.id })
        : null
      : null

  const handleExpandFullPage = () => {
    history.push(expandPath, {
      task: fetchedTask || activity,
      columns,
      users,
      departments,
      comments: fetchedComments,
    })
  }

  const expandButton = expandPath ? (
    <Tooltip title="Abrir em página inteira">
      <IconButton size="small" onClick={handleExpandFullPage}>
        <ExternalLinkIcon size={18} />
      </IconButton>
    </Tooltip>
  ) : null

  if (fullPage) {
    return (
      <>
        <Page title={pageTitle}>
          <LoadingFeedback open={loadingTask} />
          <Container maxWidth={false}>
            <ContentHeader title={title} />
            <FormContext {...formMethods}>
              <form
                id={FORM_ID}
                onSubmit={handleSubmit(onSubmit)}
                className={classes.form}
              >
                <Box className={classes.fullPageContent}>
                  {formReady && (
                    <ActivityFormContent
                      control={control}
                      errors={errors}
                      isEdit={isEdit}
                      currentColumn={currentColumn}
                      activity={activity}
                      columns={columns}
                      users={users}
                      departments={departments}
                      onRemoveAttachment={onRemoveAttachment}
                      initialComments={initialComments}
                      isMultiDepartment={isMultiDepartment}
                      isShow={isShow}
                    />
                  )}
                </Box>
              </form>
            </FormContext>
            <Box mt={2} display="flex" justifyContent="flex-end" gridGap={8}>
              <Button variant="outlined" onClick={() => setOpen(false)}>
                {canEdit ? 'Cancelar' : 'Voltar'}
              </Button>
              {canEdit && (
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  form={FORM_ID}
                  disabled={isSaving}
                  startIcon={
                    isSaving ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : null
                  }
                >
                  {isSaving ? 'Salvando…' : 'Salvar'}
                </Button>
              )}
            </Box>
          </Container>
        </Page>
        <AutomationConfirmDialog
          pendingSavePayload={pendingSavePayload}
          setPendingSavePayload={setPendingSavePayload}
          onConfirm={confirmLeaveAutomation}
        />
        <FieldEditConfirmDialog
          pendingFieldEditPayload={pendingFieldEditPayload}
          setPendingFieldEditPayload={setPendingFieldEditPayload}
          onConfirm={confirmFieldEdit}
        />
      </>
    )
  }

  return (
    <>
      <BaseDialog
        open={open}
        setOpen={setOpen}
        title={title}
        dialogSize="xl"
        fullWidth
        closeIconButton
        closeButtonText={canEdit ? 'Cancelar' : 'Fechar'}
        {...(canEdit && {
          actionButtonText: 'Salvar',
          formRef: FORM_ID,
          loading: isSaving,
        })}
        noPadding
        paperStyle={{
          borderRadius: 12,
          border: 'none',
          overflow: 'hidden',
          ...(isEdit &&
            currentColumn?.color && {
              borderTop: `4px solid ${currentColumn.color}`,
            }),
        }}
        headerActions={expandButton}
      >
        <LoadingFeedback open={loadingTask} />
        <FormContext {...formMethods}>
          <form
            id={FORM_ID}
            onSubmit={handleSubmit(onSubmit)}
            className={classes.form}
          >
            <Box className={classes.dialogContent}>
              {formReady && (
                <ActivityFormContent
                  control={control}
                  errors={errors}
                  isEdit={isEdit}
                  currentColumn={currentColumn}
                  activity={fetchedTask || activity}
                  columns={columns}
                  users={users}
                  departments={departments}
                  onRemoveAttachment={onRemoveAttachment}
                  initialComments={fetchedComments}
                  isMultiDepartment={isMultiDepartment}
                  isShow={isShow}
                />
              )}
            </Box>
          </form>
        </FormContext>
      </BaseDialog>
      <AutomationConfirmDialog
        pendingSavePayload={pendingSavePayload}
        setPendingSavePayload={setPendingSavePayload}
        onConfirm={confirmLeaveAutomation}
      />
      <FieldEditConfirmDialog
        pendingFieldEditPayload={pendingFieldEditPayload}
        setPendingFieldEditPayload={setPendingFieldEditPayload}
        onConfirm={confirmFieldEdit}
      />
    </>
  )
}

export default ActivityModal
